/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import KeywordFilter from "../filter/ui/KeywordFilter";
import { ArticleList } from "../article-list/ui/ArticleList";
import Detail from "../../detail/ui/Detail";
import { useSearchParams } from "react-router-dom";
import SortDropDown from "../search/ui/SortDropDown";
import { SORT_TYPE } from "../../../../types/enum";
import FilteredSearchComponent from "../search/ui/FilteredSearchComponent";
import { ShowFilterBtn } from "../../mobile/ShowFilterBtn";
import {
  loadLocalData,
  removeLocalData,
  saveLocalData,
} from "../../../../utils/common-util";
import { KEYS } from "../../../../constants-keys";
import { manageArticleApi } from "../../../../entities/article";
import { useTranslation } from "../../../../shared/hooks/useTranslation";

export default function Home() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isOpenedDetailModal, setIsOpenedDetailModal] = useState(false);
  const [selectedDetailId, setSelectedDetailId] = useState<
    string | undefined
  >();
  const [scrollPosition, setScrollPosition] = useState<number>(
    loadLocalData(KEYS.HOME_SCROLL_POSITION)
      ? Number(loadLocalData(KEYS.HOME_SCROLL_POSITION))
      : 0,
  );
  const [selectedArticle, setSelectedArticle] = useState<any>();
  const [selectedType, setSelectedType] = useState(SORT_TYPE.RECENT);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [onResetTriggered, setOnResetTriggered] = useState(false);
  const [resultNum, setResultNum] = useState(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [shouldRestoreScroll, setShouldRestoreScroll] = useState(false);
  const [previousSearchParams, setPreviousSearchParams] = useState<string>("");
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);

  const handleSelectedSortType = useCallback((selectedType: SORT_TYPE) => {
    setSelectedType(selectedType);
  }, []);

  // NOTE(happyhyep): 새로고침 시 스크롤 위치 유지를 위한 Effect
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const handleBeforeUnload = () => {
      if (!isOpenedDetailModal) {
        saveLocalData(KEYS.HOME_SCROLL_POSITION, String(window.scrollY));
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isOpenedDetailModal]);

  useEffect(() => {
    if (window.location.href.includes("/detail")) {
      setSelectedDetailId(window.location.href.split("/detail/")[1]);
    }
  }, []);

  useEffect(() => {
    if (selectedDetailId) {
      // 모달을 열기 전 스크롤 위치 저장
      setScrollPosition(window.scrollY);

      manageArticleApi.getArticle(selectedDetailId).then((article) => {
        setSelectedArticle(article);
      });
      setIsOpenedDetailModal(true);

      setTimeout(() => {
        window.scrollTo({ top: window.scrollY });
      }, 0);
    }
  }, [selectedDetailId]);

  const closeModal = () => {
    setIsOpenedDetailModal(false);
    setSelectedDetailId(undefined);

    // 필터링 상태가 있는지 확인
    const hasFilters = searchParams.toString().length > 0;

    if (hasFilters) {
      // 필터링 상태가 있으면 필터링된 홈페이지로 돌아가기
      const filterQuery = searchParams.toString();
      const urlWithFilters = `/?${filterQuery}`;

      window.history.pushState(null, "", urlWithFilters);

      setTimeout(() => {
        window.scrollTo({ top: scrollPosition });
      }, 0);
    } else {
      // 필터링 상태가 없으면 기본 홈페이지로 돌아가기
      window.history.pushState(null, "", window.location.origin);

      setTimeout(() => {
        window.history.pushState(null, "", window.location.origin);
        window.scrollTo({ top: scrollPosition });
      }, 0);
    }
  };

  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // 모바일 기준 너비 조정
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const openMobileMenu = () => setIsMobileMenuOpen(true);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  useEffect(() => {
    if (isOpenedDetailModal || isMobileMenuOpen) {
      const scrollY = window.scrollY; // 현재 스크롤 위치 저장
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      const scrollY = parseInt(document.body.style.top || "0", 10) * -1; // 스크롤 복원
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo({ top: scrollY, behavior: "auto" });
    }
  }, [isOpenedDetailModal, isMobileMenuOpen]);

  useEffect(() => {
    const allKeywords = searchParams.getAll("keyword");
    const allProducts = searchParams.getAll("product");
    const allEvaluations = searchParams.getAll("uxEvaluation");

    if (
      allKeywords.length > 0 ||
      allProducts.length > 0 ||
      allEvaluations.length > 0
    ) {
      setShowSearchBar(true);
    } else {
      setShowSearchBar(false);
    }
  }, [searchParams]);

  // searchParams 변경 시 스크롤 초기화 (필터링이 있을 때만)
  useEffect(() => {
    const allKeywords = searchParams.getAll("keyword");
    const allProducts = searchParams.getAll("product");
    const allEvaluations = searchParams.getAll("uxEvaluation");

    // 필터가 적용된 경우에만 스크롤을 맨 위로
    if (
      allKeywords.length > 0 ||
      allProducts.length > 0 ||
      allEvaluations.length > 0
    ) {
      window.scrollTo(0, 0);
      setShouldRestoreScroll(false);
    } else {
      // 필터가 없는 경우 스크롤 복원 필요
      setShouldRestoreScroll(true);
    }
  }, [searchParams]);

  const handleResetFilters = useCallback(() => {
    setOnResetTriggered(true);
    setShowSearchBar(false);
  }, []);

  const handleResultCountChange = useCallback((count: number) => {
    setResultNum(count);
  }, []);

  // 데이터 로딩 완료 상태를 추적하는 함수
  const handleDataLoaded = useCallback(() => {
    setIsDataLoaded(true);
  }, []);

  // 스크롤 위치 복원 (데이터 로딩 완료 후)
  useEffect(() => {
    if (isDataLoaded && shouldRestoreScroll && scrollPosition > 0) {
      setIsRestoringScroll(true);

      // 모바일에서는 더 긴 지연을 두어 무한 스크롤과 충돌 방지
      const delay = isMobile ? 300 : 100;
      const timer = setTimeout(() => {
        window.scrollTo({ top: scrollPosition, behavior: "auto" });
        setShouldRestoreScroll(false);

        // 스크롤 복원 완료 후 무한 스크롤 재활성화
        setTimeout(() => {
          setIsRestoringScroll(false);
        }, 200);
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isDataLoaded, shouldRestoreScroll, scrollPosition, isMobile]);

  return (
    <section className="home-page">
      {isOpenedDetailModal ? (
        <Detail
          key={selectedDetailId}
          id={selectedDetailId}
          data={selectedArticle}
          closeModal={closeModal}
        />
      ) : null}
      {isOpenedDetailModal ? (
        <div className="dim" onClick={closeModal}></div>
      ) : null}

      <div className="bottom-view">
        <KeywordFilter
          onReset={onResetTriggered}
          setOnResetTriggered={setOnResetTriggered}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          closeMobileMenu={closeMobileMenu}
        />
        <div className="filter-right-view">
          <div className="filtered-summary">
            <SortDropDown
              handleSelectedSortType={handleSelectedSortType}
              selectedType={selectedType}
            />
            <div className="result-num">
              {t("results.total", { count: resultNum })}
            </div>
          </div>
          {isMobile && (
            <div onClick={openMobileMenu}>
              <ShowFilterBtn />
            </div>
          )}
          {showSearchBar ? (
            <FilteredSearchComponent onReset={handleResetFilters} />
          ) : null}
          <ArticleList
            selectedType={selectedType}
            onArticleClick={setSelectedDetailId}
            onModalOpen={setIsOpenedDetailModal}
            onResultCountChange={handleResultCountChange}
            onDataLoaded={handleDataLoaded}
            isRestoringScroll={isRestoringScroll}
          />
        </div>
      </div>
      {isMobile && isMobileMenuOpen ? (
        <div
          className="mobile-dim"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      ) : null}
    </section>
  );
}
